import React, { useEffect, useState } from 'react';

interface Props {
  /** Legacy / direct URL (often presigned S3). Avoid using for playback when it fails in-browser. */
  openHref: string;
  fileName?: string;
  /** Same-origin API media URL — always prefer fetch + Bearer for playback/download. */
  playbackUrl?: string | null;
}

/**
 * Playback: fetch bytes with JWT (same as rest of SPA), then blob URL — works when public S3 / presigned GET is blocked or broken.
 */
export function ConversationVoiceAttachment({ openHref, fileName, playbackUrl }: Props) {
  const trimmedOpen = openHref.trim();
  const trimmedPlayback = playbackUrl?.trim() ?? '';

  const [blobPlaybackUrl, setBlobPlaybackUrl] = useState<string | null>(() =>
    trimmedPlayback ? null : trimmedOpen || null,
  );
  /** True when API stream was attempted and failed — do not trust presigned for &lt;audio&gt; either. */
  const [authStreamFailed, setAuthStreamFailed] = useState(false);
  const [playerFailed, setPlayerFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!trimmedPlayback) {
      setBlobPlaybackUrl(trimmedOpen || null);
      setAuthStreamFailed(false);
      return () => {};
    }

    setBlobPlaybackUrl(null);
    setAuthStreamFailed(false);

    void (async () => {
      const token =
        typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setAuthStreamFailed(true);
        return;
      }
      try {
        const res = await fetch(trimmedPlayback, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          mode: 'cors',
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setBlobPlaybackUrl(blobUrl);
      } catch {
        if (!cancelled) setAuthStreamFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [trimmedOpen, trimmedPlayback]);

  useEffect(() => {
    const url = blobPlaybackUrl;
    return () => {
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };
  }, [blobPlaybackUrl]);

  const suggestName =
    fileName?.trim() || 'voice-message.m4a';

  async function authenticatedDownload(): Promise<void> {
    const urlToFetch = trimmedPlayback || null;
    if (!urlToFetch) return;
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) {
      alert('You need to stay signed in to download this attachment.');
      return;
    }
    setDownloading(true);
    try {
      const res = await fetch(urlToFetch, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        mode: 'cors',
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = suggestName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      alert('Download failed. If this keeps happening, the API cannot read this file from storage (check S3 permissions / object exists).');
    } finally {
      setDownloading(false);
    }
  }

  if (!trimmedOpen && !trimmedPlayback) return null;

  /** API returned an error — show recovery UI instead of pretending a broken presigned URL will work */
  if (trimmedPlayback && authStreamFailed && !blobPlaybackUrl) {
    return (
      <VoiceFailureNotice
        onAuthenticatedDownload={
          trimmedPlayback ? () => void authenticatedDownload() : undefined
        }
        downloading={downloading}
        presignedHref={trimmedOpen}
        suggestedName={suggestName}
        showBrokenPresignedHint
      />
    );
  }

  if (trimmedPlayback && !blobPlaybackUrl && !authStreamFailed) {
    return (
      <div className="min-w-0 flex-1 text-xs text-neutral-500 py-2 shrink-0">
        Loading voice message…
      </div>
    );
  }

  if (!blobPlaybackUrl) return null;

  if (playerFailed) {
    return (
      <VoiceFailureNotice
        onAuthenticatedDownload={
          trimmedPlayback ? () => void authenticatedDownload() : undefined
        }
        downloading={downloading}
        presignedHref={trimmedOpen}
        suggestedName={suggestName}
        showBrokenPresignedHint={Boolean(trimmedOpen)}
      />
    );
  }

  return (
    <audio
      controls
      preload="metadata"
      className="min-w-0 flex-1 w-full min-h-[44px] max-h-14"
      src={blobPlaybackUrl}
      onError={() => setPlayerFailed(true)}
    >
      Your browser does not support the audio element.
    </audio>
  );
}

function VoiceFailureNotice({
  presignedHref,
  suggestedName,
  onAuthenticatedDownload,
  downloading,
  showBrokenPresignedHint,
}: {
  presignedHref: string;
  suggestedName: string;
  onAuthenticatedDownload?: () => void;
  downloading: boolean;
  showBrokenPresignedHint: boolean;
}) {
  return (
    <div className="min-w-0 flex-1 flex flex-col gap-2 rounded border border-amber-200 bg-amber-50/90 p-2">
      <p className="text-xs font-medium text-amber-950">
        This clip could not be played automatically.
      </p>
      {onAuthenticatedDownload && (
        <button
          type="button"
          disabled={downloading}
          onClick={onAuthenticatedDownload}
          className="rounded bg-purple-700 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60 shrink-0 w-fit"
        >
          {downloading ? 'Preparing download…' : `Download (${suggestedName})`}
        </button>
      )}
      <p className="text-[11px] text-neutral-700">
        Download uses your Staffly login and loads the file from the API — it works even when a public Amazon S3 link returns Access Denied.
      </p>
      {showBrokenPresignedHint && presignedHref && (
        <details className="text-[11px] text-neutral-600">
          <summary className="cursor-pointer underline">Technical: presigned Amazon link</summary>
          <p className="mt-1">
        If opening the long amazonaws.com URL in a new tab also fails with Access Denied or NoSuchKey, the object may be missing from the
        bucket, or the IAM principal that generates presigned URLs may lack{' '}
        <code className="bg-white/80 px-0.5">s3:GetObject</code> (and KMS decrypt if objects use SSE-KMS).
        Fixing that only affects sharable links; the download button above uses server-side reads and succeeds whenever the API can read the object.
          </p>
          <p className="mt-1 break-all opacity-70">{presignedHref.slice(0, 120)}…</p>
        </details>
      )}
    </div>
  );
}
